// @ts-nocheck
import { useState, useMemo } from 'react';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import {
  Stack,
  Paper,
  Group,
  Text,
  Button,
  Select,
  MultiSelect,
  SimpleGrid,
  Table,
  Loader,
  Center,
  Badge,
} from '@mantine/core';
// eslint-disable-next-line ferp-subgraph-ui/no-direct-mantine-import
import { notifications } from '@mantine/notifications';
import {
  IconFileExport,
  IconChartBar,
  IconCurrencyDollar,
  IconArrowsDiff,
} from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { StatCard } from '../../components/StatCard';
import { EmptyState } from '../../components/EmptyState';
import {
  useJobs,
  useJobCostReport,
  useExportJobCostReport,
  type JobCostReportRow,
} from '../../hooks/useJobCosting';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

function getDefaultDateRange(): { startDate: string; endDate: string } {
  const now = new Date();
  const endDate = now.toISOString().split('T')[0] ?? '';
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const startDate = startOfYear.toISOString().split('T')[0] ?? '';
  return { startDate, endDate };
}

export function JobCostReportPage() {
  const defaults = getDefaultDateRange();
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [selectedJobIds, setSelectedJobIds] = useState<string[]>([]);
  const [departmentFilter, setDepartmentFilter] = useState<string | null>(null);

  const { data: jobsData } = useJobs({ perPage: 200 });
  const exportMutation = useExportJobCostReport();

  const jobOptions = useMemo(() => {
    if (!jobsData) return [];
    return (jobsData as any[]).map((j: any) => ({
      value: j.id,
      label: `${j.code} - ${j.name}`,
    }));
  }, [jobsData]);

  const departmentOptions = useMemo(() => {
    if (!jobsData) return [];
    const depts = new Map<string, string>();
    for (const job of jobsData as any[]) {
      if (job.departmentId && job.departmentName) {
        depts.set(job.departmentId, job.departmentName);
      }
    }
    return Array.from(depts.entries()).map(([value, label]) => ({ value, label }));
  }, [jobsData]);

  const { data: reportDataRaw, isLoading: reportLoading } = useJobCostReport({
    startDate,
    endDate,
    jobIds: selectedJobIds.length > 0 ? selectedJobIds : undefined,
    departmentId: departmentFilter || undefined,
  });
  const reportData = reportDataRaw as any;

  const handleExport = (format: 'CSV' | 'PDF') => {
    exportMutation.mutate(
      {
        startDate,
        endDate,
        jobIds: selectedJobIds.length > 0 ? selectedJobIds : undefined,
        departmentId: departmentFilter || undefined,
        format,
      },
      {
        onSuccess: (data: any) => {
          if (data.exportJobCostReport.downloadUrl) {
            window.open(data.exportJobCostReport.downloadUrl, '_blank');
          }
          notifications.show({
            title: 'Export Ready',
            message: `${format} report has been generated.`,
            color: 'green',
          });
        },
        onError: (err: any) => {
          notifications.show({
            title: 'Export Failed',
            message: err instanceof Error ? err.message : 'Failed to export report',
            color: 'red',
          });
        },
      },
    );
  };

  const getVarianceColor = (variance: number): string => {
    if (variance > 0) return 'green';
    if (variance < 0) return 'red';
    return 'gray';
  };

  return (
    <Stack gap="md">
      <PageHeader
        title="Job Cost Report"
        breadcrumbs={[
          { label: 'Job Costing', to: '/admin/workforce/jobs' },
          { label: 'Cost Report' },
        ]}
        subtitle="Compare budgets vs actual costs across jobs"
        actions={
          <Group gap="sm">
            <Button
              variant="light"
              icon={<IconFileExport size={16} />}
              onClick={() => handleExport('CSV')}
              loading={exportMutation.isPending}
              disabled={!reportData}
            >
              Export CSV
            </Button>
            <Button
              variant="light"
              icon={<IconFileExport size={16} />}
              onClick={() => handleExport('PDF')}
              loading={exportMutation.isPending}
              disabled={!reportData}
            >
              Export PDF
            </Button>
          </Group>
        }
      />

      {/* -- Filters --------------------------------------------------------- */}
      <Paper p="md" withBorder>
        <Text size="sm" fw={600} mb="sm">
          Report Filters
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
          <div>
            <Text size="xs" c="dimmed" mb={4}>Start Date</Text>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid var(--mantine-color-gray-4)',
                borderRadius: 'var(--mantine-radius-sm)',
                fontSize: '14px',
                backgroundColor: 'var(--mantine-color-body)',
                color: 'var(--mantine-color-text)',
              }}
            />
          </div>
          <div>
            <Text size="xs" c="dimmed" mb={4}>End Date</Text>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid var(--mantine-color-gray-4)',
                borderRadius: 'var(--mantine-radius-sm)',
                fontSize: '14px',
                backgroundColor: 'var(--mantine-color-body)',
                color: 'var(--mantine-color-text)',
              }}
            />
          </div>
          <MultiSelect
            label="Jobs"
            placeholder="All jobs"
            data={jobOptions}
            value={selectedJobIds}
            onChange={setSelectedJobIds}
            clearable
            searchable
            maxDropdownHeight={200}
          />
          <Select
            label="Department"
            placeholder="All departments"
            data={departmentOptions}
            value={departmentFilter}
            onChange={setDepartmentFilter}
            clearable
          />
        </SimpleGrid>
      </Paper>

      {/* -- Summary Stats --------------------------------------------------- */}
      {reportData && (
        <SimpleGrid cols={{ base: 1, sm: 3 }}>
          <StatCard
            title="Total Budget"
            value={formatCurrency(reportData.grandTotalBudget)}
            icon={<IconCurrencyDollar size={24} />}
            color="blue"
          />
          <StatCard
            title="Total Cost"
            value={formatCurrency(reportData.grandTotalCost)}
            icon={<IconChartBar size={24} />}
            color="orange"
          />
          <StatCard
            title="Total Variance"
            value={formatCurrency(reportData.grandTotalVariance)}
            icon={<IconArrowsDiff size={24} />}
            color={reportData.grandTotalVariance >= 0 ? 'green' : 'red'}
          />
        </SimpleGrid>
      )}

      {/* -- Report Table ---------------------------------------------------- */}
      {reportLoading ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : !reportData || reportData.jobs.length === 0 ? (
        <EmptyState
          icon={<IconChartBar size={48} stroke={1.5} color="var(--mantine-color-dimmed)" />}
          title="No Report Data"
          message="Adjust the date range or filters to generate a report. Ensure jobs have cost entries within the selected period."
        />
      ) : (
        <Paper withBorder>
          <Table.ScrollContainer minWidth={1000}>
            <Table stickyHeader striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Job</Table.Th>
                  <Table.Th ta="right">Budget</Table.Th>
                  <Table.Th ta="right">Labor</Table.Th>
                  <Table.Th ta="right">Materials</Table.Th>
                  <Table.Th ta="right">Equipment</Table.Th>
                  <Table.Th ta="right">Overhead</Table.Th>
                  <Table.Th ta="right">Other</Table.Th>
                  <Table.Th ta="right">Total Cost</Table.Th>
                  <Table.Th ta="right">Variance</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {reportData.jobs.map((row: JobCostReportRow) => (
                  <Table.Tr key={row.jobId}>
                    <Table.Td>
                      <Stack gap={0}>
                        <Text size="sm" fw={500}>{row.jobName}</Text>
                        <Text size="xs" c="dimmed" ff="monospace">{row.jobCode}</Text>
                      </Stack>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">{formatCurrency(row.budgetAmount)}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">{formatCurrency(row.totalLabor)}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">{formatCurrency(row.totalMaterial)}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">{formatCurrency(row.totalEquipment)}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">{formatCurrency(row.totalOverhead)}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm">{formatCurrency(row.totalOther)}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" fw={600}>{formatCurrency(row.totalCost)}</Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Badge
                        color={getVarianceColor(row.variance)}
                        variant="light"
                        size="lg"
                      >
                        {row.variance >= 0 ? '+' : ''}
                        {formatCurrency(row.variance)}
                      </Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}

                {/* -- Totals Row ------------------------------------------------ */}
                <Table.Tr
                  style={{
                    borderTop: '2px solid var(--mantine-color-gray-4)',
                    fontWeight: 700,
                  }}
                >
                  <Table.Td>
                    <Text size="sm" fw={700}>TOTALS</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={700}>
                      {formatCurrency(reportData.grandTotalBudget)}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={700}>
                      {formatCurrency(
                        reportData.jobs.reduce((sum: number, r: JobCostReportRow) => sum + r.totalLabor, 0),
                      )}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={700}>
                      {formatCurrency(
                        reportData.jobs.reduce((sum: number, r: JobCostReportRow) => sum + r.totalMaterial, 0),
                      )}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={700}>
                      {formatCurrency(
                        reportData.jobs.reduce((sum: number, r: JobCostReportRow) => sum + r.totalEquipment, 0),
                      )}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={700}>
                      {formatCurrency(
                        reportData.jobs.reduce((sum: number, r: JobCostReportRow) => sum + r.totalOverhead, 0),
                      )}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={700}>
                      {formatCurrency(
                        reportData.jobs.reduce((sum: number, r: JobCostReportRow) => sum + r.totalOther, 0),
                      )}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={700}>
                      {formatCurrency(reportData.grandTotalCost)}
                    </Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Badge
                      color={getVarianceColor(reportData.grandTotalVariance)}
                      variant="light"
                      size="lg"
                    >
                      {reportData.grandTotalVariance >= 0 ? '+' : ''}
                      {formatCurrency(reportData.grandTotalVariance)}
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      )}
    </Stack>
  );
}
